import { BaseWebComponentView } from '../../../../../framework/BaseWebComponentView';
import { GridViewWebComponent } from '../../../../../grid/gridView';
import { GzIconButtonWebComponent } from '../../../../../ui/gzIconButton';
import { GzFormFieldWebComponent } from '../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../ui/gzInput/component/GzInputWebComponent';
import { GzInfoBoxWebComponent } from '../../../../../ui/gzInfoBox';
import { WEB_COMPONENT_GRID_VIEW } from '../../../../../webcomponents.constants';
import { ColumnDetails, ColumnType } from '../../../../../grid/types/common';
import {
  AuthTypesData,
  Column,
  ColumnKeys,
  CredentialGridData,
  CredentialsManagerEvents,
  CredentialsManagerGridSchema,
  pageSizeOptions,
  PEM_SIZE_LIMIT_KB,
} from '../types';
import { GzImageRepoWebComponent } from '../../../../../ui/gzImageRepo';
import { ImageState } from '../../../../../ui/gzImageRepo/types';
import { FormGroup } from '../../../../../ui/gzForm/formGroup/form-group';
import { CredentialsManagerGridTableDataProvider } from '../dataProvider/CredentialsManagerGridTableDataProvider';
import { ValidationResult } from '../../../../../ui/gzForm/formField/types/form-validator';
import { toggleVisibility } from '../../../../../utils/htmlElement.utils';
import { CustomizableValidator } from '../../../../../ui/gzForm/validators/customizable.validator';
import { GzDropdownWebComponent } from '../../../../../ui/gzDropdown';
import { translateFn } from '../../../../../utils/translator.helper';
import { GzTextWebComponent } from '../../../../../ui/gzText';
import { IntegrationType } from '../../../../../company/types/IntegrationType';
import { GzInputFileWebComponent } from '../../../../../ui/gzInputFile';
import { GzButtonWebComponent } from '../../../../../ui/gzButton';
import { RequiredValidator } from '../../../../../ui/gzForm/validators/required.validator';
import { Subscription } from 'rxjs';

export class CredentialsManagerGridView extends BaseWebComponentView {
  public formGroup!: FormGroup;
  public addFormGroup!: FormGroup;
  public isGridValid = false;
  public hasEc2Enabled = true;
  public authTypes: Array<AuthTypesData>;
  private formGroupSubscription: Subscription | null = null;

  public dom: {
    $wrapper: HTMLElement;
    $gridView: GridViewWebComponent<Column, ColumnKeys>;
    $addCredentialsButton: GzIconButtonWebComponent;
    $userFormField: GzFormFieldWebComponent<GzInputWebComponent>;
    $userInnerInput: GzInputWebComponent;
    $authenticationTypeFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $pemFilesField: GzFormFieldWebComponent<GzInputFileWebComponent>;
    $browsePemFilesButton: GzButtonWebComponent;
    $pemFilePathField: GzInputWebComponent;
    $passwordFormField: GzFormFieldWebComponent<GzInputWebComponent>;
    $passwordInnerInput: GzInputWebComponent;
    $descriptionFormField: GzFormFieldWebComponent<GzInputWebComponent>;
    $descriptionInnerInput: GzInputWebComponent;
    $noResultsSubtitle: HTMLElement;
    $sizeHelperWrapper: HTMLElement;
    $sizeHelperText: HTMLElement;
    $gridBoxInfo: GzInfoBoxWebComponent;
    $gridFormFieldValidation: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $infoNote: GzTextWebComponent;
    $fileInput: GzInputFileWebComponent;
  };

  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$gridView = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW);
    this.dom.$addCredentialsButton = this.shadow.querySelector('#add-credentials-button');
    this.dom.$userFormField = this.shadow.querySelector('#user-field');
    this.dom.$userInnerInput = this.shadow.querySelector('#user-input');
    this.dom.$authenticationTypeFormField = this.shadow.querySelector('#authentication-type-field');
    this.dom.$pemFilesField = this.shadow.querySelector('#pem-files-field');
    this.dom.$browsePemFilesButton = this.shadow.querySelector('#browse-pem-files-button');
    this.dom.$pemFilePathField = this.shadow.querySelector('#pem-file-path-field');
    this.dom.$passwordFormField = this.shadow.querySelector('#password-field');
    this.dom.$passwordInnerInput = this.shadow.querySelector('#password-input');
    this.dom.$descriptionFormField = this.shadow.querySelector('#description-field');
    this.dom.$descriptionInnerInput = this.shadow.querySelector('#description-input');
    this.dom.$noResultsSubtitle = this.shadow.querySelector('#no-results-subtitle');
    this.dom.$sizeHelperWrapper = this.shadow.querySelector('#size-helper-wrapper');
    this.dom.$sizeHelperText = this.shadow.querySelector('#size-helper-text');
    this.dom.$gridBoxInfo = this.shadow.querySelector('#credentials-manager-grid-box-info');
    this.dom.$gridFormFieldValidation = this.shadow.querySelector('#credentials-manager-form-field-validation');
    this.dom.$infoNote = this.shadow.querySelector('#credentials-manager-info-note');
    this.dom.$fileInput = this.shadow.querySelector('gz-file-input');

    this.setTranslatedStrings();

    if (this.dom.$gridView.injectDependencies) {
      this.dom.$gridView.injectDependencies(this.dependencies);
      this.dom.$gridView.start().then().catch(console.error);
    }
  }

  private getColumnDetails(): Array<ColumnDetails<Column, ColumnKeys>> {
    return [
      {
        column: Column.USER,
        key: ColumnKeys.USER,
        columnName: this.dependencies.translateFn('User'),
        type: ColumnType.STRING,
        editingDisabled: true,
        removable: false,
      },
      {
        column: Column.PASSWORD,
        key: ColumnKeys.PASSWORD,
        columnName: this.hasEc2Enabled
          ? this.dependencies.translateFn('Password/PEM')
          : this.dependencies.translateFn('Password'),
        type: ColumnType.STRING,
        editingDisabled: true,
        removable: false,
      },
      {
        column: Column.DESCRIPTION,
        key: ColumnKeys.DESCRIPTION,
        columnName: this.dependencies.translateFn('Description'),
        type: ColumnType.STRING,
        editingDisabled: true,
        removable: false,
      },
      {
        column: Column.ACTIONS,
        key: ColumnKeys.ACTIONS,
        columnName: this.dependencies.translateFn('Actions'),
        type: ColumnType.STRING,
        removable: false,
      },
    ];
  }

  setTranslatedStrings(): void {
    this.dom.$userInnerInput.placeholder = translateFn('User');
    this.dom.$browsePemFilesButton.innerText = translateFn('Browse');
    this.dom.$passwordInnerInput.placeholder = translateFn('Password');
    this.dom.$descriptionInnerInput.placeholder = translateFn('Description');
    this.dom.$noResultsSubtitle.innerText = translateFn("You haven't entered any user information yet.");
    this.dom.$sizeHelperText.innerText = translateFn('The size limit is ' + PEM_SIZE_LIMIT_KB + 'kb.');
    this.dom.$gridBoxInfo.innerText = translateFn('Please select at least one credentials set');
    this.dom.$infoNote.innerText = translateFn(
      'For Active Directory machines, enter the domain usernames as USERNAME@DOMAIN (when deploying through a relay) or DOMAIN\\USERNAME, where DOMAIN is the NetBios name of the domain. For Workgroups machines, it suffices the enter only the username, without the workgroup name.'
    );
  }

  buildSuccessView(data: CredentialGridData): void {
    this.authTypes = data.authTypes;
    this.createFormGroup();
    this.createAddFormGroup();
    this.initializeGridHeaderActions();
    this.initializeCredentialsGrid();
    this.setValidators();

    this.formGroupSubscription = this.addFormGroup.valid$.subscribe({
      next: valid => {
        this.dom.$addCredentialsButton.disabled = !valid;
      },
    });
  }

  public setValidators(): void {
    this.dom.$gridFormFieldValidation.Validators = [
      CustomizableValidator(this.customValidationGridSelection.bind(this)),
    ];

    this.addRequiredValidator(this.dom.$userFormField);
    if (this.hasEc2Enabled) {
      this.addRequiredValidator(this.dom.$authenticationTypeFormField);
    } else {
      this.addRequiredValidator(this.dom.$passwordFormField);
    }
  }

  public removeValidators(field: GzFormFieldWebComponent): void {
    field.Validators = [];
  }

  public addRequiredValidator(field: GzFormFieldWebComponent): void {
    field.Validators = [RequiredValidator('')];
  }

  public createAddFormGroup(): void {
    this.addFormGroup = FormGroup.build({
      username: this.dom.$userFormField,
      auth_type: this.dom.$authenticationTypeFormField,
      password: this.dom.$passwordFormField,
      pem: this.dom.$pemFilesField,
    });
  }

  private createFormGroup(): void {
    this.formGroup = FormGroup.build({
      credentials: {
        validation: this.dom.$gridFormFieldValidation,
      },
    });
  }

  private initializeGridHeaderActions(): void {
    this.hasEc2Enabled =
      this.dependencies.isCloud() &&
      this.dependencies.IntegrationsLib.self.hasCloudIntegrationConfigured(IntegrationType.Aws);
    this.setAuthOptions();
    toggleVisibility(this.dom.$authenticationTypeFormField, this.hasEc2Enabled);
    toggleVisibility(this.dom.$passwordFormField, !this.hasEc2Enabled);
    toggleVisibility(this.dom.$pemFilesField, false);
  }

  private setAuthOptions(): void {
    this.dom.$authenticationTypeFormField.Input.placeholder = translateFn('Select authentication');
    this.dom.$authenticationTypeFormField.Input.label = translateFn('Select authentication');
    this.dom.$authenticationTypeFormField.Input.options = this.authTypes;
  }

  private initializeCredentialsGrid(): void {
    this.dom.$gridView.propertiesSet = false;

    this.dom.$gridView.columnRenderers = {
      [ColumnKeys.ACTIONS]: this.renderActions.bind(this),
    };
    this.dom.$gridView.viewId = 'credentials-manager';
    this.dom.$gridView.dataProvider = new CredentialsManagerGridTableDataProvider(
      this.dependencies,
      this.getColumnDetails()
    );
    this.dom.$gridView.allColumnDetails = this.getColumnDetails();
    this.dom.$gridView.columns = [0, 1, 2, 3];
    this.dom.$gridView.hasSelectableRows = true;
    this.dom.$gridView.hasPagination = true;
    this.dom.$gridView.pageSize = 20;
    this.dom.$gridView.pageSizeOptions = pageSizeOptions;
    this.dom.$gridView.pageNumber = 1;
    this.dom.$gridView.isCompactViewChecked = false;
    this.dom.$gridView.removeFilters = true;
    this.dom.$gridView.hideHeaderActions = true;
    this.dom.$gridView.hideGridRefresh = true;
    const noResultsContainer = this.dom.$gridView.shadowRoot?.querySelector(
      '.no-results gz-image-repo'
    ) as GzImageRepoWebComponent;

    noResultsContainer.state = ImageState.START;
    this.dom.$gridView.customStrings = {
      noSearchResultsString: this.dependencies.translateFn('You can start adding user data'),
    };

    this.dom.$gridView.propertiesSet = true;
  }

  private renderActions(rowData: Array<CredentialsManagerGridSchema>): HTMLElement {
    const container: HTMLElement = document.createElement('actions');

    let deleteDocumentFragment;

    deleteDocumentFragment = this.createIconButton(
      'delete-credential-button',
      'ds-icon-clear',
      'secondary',
      '20',
      'round',
      'quiet',
      '24'
    );

    const deleteButton = deleteDocumentFragment.querySelector('#delete-credential-button');
    deleteButton?.addEventListener('click', () => {
      const event = new CustomEvent(CredentialsManagerEvents.OnDeleteCredential, { detail: { rowData } });
      this.dom.$gridView.dispatchEvent(event);
    });
    container.append(deleteDocumentFragment);
    return container;
  }

  private createIconButton(
    id: string,
    icon: string,
    buttoncolor: string,
    fontsize: string,
    buttontype: string,
    buttonfill: string,
    size: string
  ): DocumentFragment {
    return this.createTemplateElement(`<gz-icon-button
      id = '${id}'
      icon='${icon}'
      buttoncolor='${buttoncolor}'
      fontsize='${fontsize}'
      buttontype='${buttontype}'
      buttonfill='${buttonfill}'
      size='${size}'
    ></gz-icon-button>`).content;
  }

  private createTemplateElement(html: string): HTMLTemplateElement {
    const templateElement = document.createElement('template');
    templateElement.innerHTML = html;

    return templateElement;
  }

  private customValidationGridSelection(): ValidationResult {
    if (!this.isGridValid) {
      toggleVisibility(this.dom.$gridBoxInfo, true);
      return {
        valid: false,
        error: '',
      };
    }
    toggleVisibility(this.dom.$gridBoxInfo, false);
    return {
      valid: true,
    };
  }

  setLoadingState(): void {
    this.dom.$gridView.controller?.disableDataLoading(true);
    this.dom.$gridView.controller?.setMoreItemsLoadingState(true);
  }

  removeLoadingState(): void {
    this.dom.$gridView.controller?.disableDataLoading(false);
    this.dom.$gridView.controller?.setMoreItemsLoadingState(false);
  }
}
